import _ from 'lodash';
import { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  GET_CURR_MEMBER,
  GET_MEMBERS,
  MEMBER_FIELDS,
  RESTORE_MEMBER_ACCESS,
  REVOKE_MEMBER_ACCESS
} from '../queries/member.queries';
import { createCacheModifier, isInCachedList, removeCacheModifier } from '../cache/basic.cache';
import { getAuthUser } from './auth.service';

export const getCurrMember = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, ...rest } = useQuery(GET_CURR_MEMBER, {
    fetchPolicy: 'cache-and-network',
  });

  return { ...rest, member: _.get(data, 'getCurrMember') as IMember };
};

export const buildRevokeMemberAccess = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REVOKE_MEMBER_ACCESS);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const revokeMemberAccess = useCallback((_id: string, ageGroupId?: string) =>
      mutation({
        variables: { _id, ageGroupId },
        update: (cache, { data }) => {
          if (data) {
            const revokedMember = data.revokeMemberAccess;
            const wasListed = isInCachedList(cache, 'members', _id);

            cache.modify({
              fields: {
                members: removeCacheModifier(cache, _id),
                archivedMembers: createCacheModifier({
                  cache,
                  createdDoc: revokedMember,
                  modelName: 'Member',
                  fragment: MEMBER_FIELDS,
                  fragmentName: 'MemberFields'
                }),
                countArchivedMembers: (count: number) => wasListed ? count + 1 : count
              }
            });
          }
        },
      }), [mutation]);

  return { ...rest, revokeMemberAccess };
};

export const buildRestoreMemberAccess = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(RESTORE_MEMBER_ACCESS);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const restoreMemberAccess = useCallback((_id: string, ageGroupId?: string) => mutation({
      variables: { _id, ageGroupId },
      update: (cache, { data }) => {
        if (data) {
          const restoredMember = data.restoreMemberAccess;
          const wasListed = isInCachedList(cache, 'archivedMembers', _id);

          cache.modify({
            fields: {
              members: createCacheModifier({
                cache,
                createdDoc: restoredMember,
                modelName: 'Member',
                fragment: MEMBER_FIELDS,
                fragmentName: 'MemberFields'
              }),
              archivedMembers: removeCacheModifier(cache, _id),
              countArchivedMembers: (count: number) => wasListed ? Math.max(0, count - 1) : count
            }
          })
        }
      },
    }), [mutation]);

  return { ...rest, restoreMemberAccess };
};

export const getMembers = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, ...rest } = useQuery(GET_MEMBERS, {
    skip: !user,
    variables: { companyId: user?.settings.activeCompanyId },
    fetchPolicy: 'cache-and-network'
  });

  return { ...rest, members: _.get(data, 'members', []) };
};
